<template>
  <RootPageLayout
    class="library-page"
    title=""
    :subtitle="docsListView?.pageTitle()"
  >
    <template #actions>
      <BackLink class="docs-listing__backlink container" />
      <div v-if="showCustomListMenu" class="docs-listing__header-menu">
        <CustomListMenu
          v-if="docsListView?.customList"
          position="right"
          :list="docsListView.customList"
          :listsView="customListsView"
        />
      </div>
      <div
        v-if="
          docsListView &&
          !wait.is('Loading List') &&
          !wait.is('Error Loading List')
        "
        class="docs-listing__sortfilter docs-listing__sortfilter--header"
      >
        <DocsSort
          :view="docsListView.docs() as DocsView"
          newStyle20321B
          :useQueryString="false"
        />
        <DocsFilterType
          :view="docsListView.docs() as DocsView"
          newStyle20321B
        />
      </div>
    </template>

    <div v-if="wait.is('Loading List')" class="layout__wrapper-inner container">
      <LoadingSpinner />
    </div>

    <div
      v-else-if="wait.is('Error Loading List')"
      class="layout__wrapper-inner container"
    >
      <LoadingError />
    </div>

    <div v-else class="layout__wrapper-inner container docs-listing__content">
      <div v-if="docsListView">
        <!-- On mobile, show the sort and filter dropdowns below the header. -->
        <div class="docs-listing__sortfilter docs-listing__sortfilter--body">
          <DocsSort
            :view="docsListView.docs() as DocsView"
            newStyle20321B
            :useQueryString="false"
          />
          <DocsFilterType
            :view="docsListView.docs() as DocsView"
            newStyle20321B
          />
        </div>
        <ListDocs
          :view="docsListView"
          :showDocSort="false"
          :listsView="customListsView"
        />
      </div>
    </div>
  </RootPageLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, Raw } from 'vue'
import {
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  useRouter,
} from 'vue-router'

import RootPageLayout from '@/app/components/RootPageLayout.vue'
import BackLink from '@/app/components/BackLink.vue'
import DocsFilterType from '@/app/components/listing/DocsFilterType.vue'
import DocsSort from '@/app/components/listing/DocsSort.vue'
import ListDocs from '@/app/components/listing/ListDocs.vue'
import CustomListMenu from '@/app/components/custom.lists/CustomListMenu.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import { CustomListsView, isEditableCustomList } from '@/models/custom.lists'
import { DocsView } from '@/models/doc'
import { ListView, createListViewByRoute20321 } from '@/models/docs.list'
import { useWait } from '@/app/composables/useWait'
import { Sentry } from '@/services/sentry'
import type {
  CustomListWithDocs,
  Doc,
  DocsUserDataResponse,
} from '@/models/interfaces'

const wait = useWait()
const router = useRouter()

const props = defineProps<{
  docs: Doc[]
  docsUserData: DocsUserDataResponse
  lists: CustomListWithDocs[]
}>()

const docsListView = ref<Raw<ListView> | null>(null)

// This view is passed to the Rename/Delete widget so that they can
// modify the prop.lists.
const customListsView = ref<Raw<CustomListsView>>(
  new CustomListsView(props.lists),
)

onBeforeMount(async () => {
  await createView(router.currentRoute.value)
})

onBeforeRouteUpdate(async (to) => {
  // We don't have any links on the page which navigate to a different
  // library list. So this only triggers when the sort/filter changes,
  // which is not strictly necessary. But it's not a significant delay
  // either. createListViewByRoute20321() does not need to hit the back
  // end.
  await createView(to)
})

async function createView(route: RouteLocationNormalized): Promise<void> {
  const routeName = route.name as string
  const params = route.params

  try {
    wait.start('Loading List')
    wait.end('Error Loading List')

    docsListView.value = await createListViewByRoute20321(
      routeName,
      params,
      props.docs,
      props.docsUserData,
      props.lists,
    )
  } catch (error: any) {
    console.error(error)
    wait.start('Error Loading List')
    Sentry.captureException(error)
  } finally {
    wait.end('Loading List')
  }
}

/**
 * Return whether or not the current page is a non-standard Custom List listing page.
 */
const showCustomListMenu = computed(() =>
  isEditableCustomList(docsListView.value?.customList),
)
</script>

<style lang="scss" scoped>
.docs-listing {
  &__backlink {
    padding-left: 0px;
  }
  &__header-menu {
    position: absolute;
    bottom: 20px;
    right: 26px;
    @media (min-width: $w-tablet-b) {
      display: none;
    }
  }
  &__content {
    @media (max-width: $w-tablet-s) {
      // This makes the sort/filter dropdowns equidistant from the header and
      // the first card.
      padding-top: 16px;
    }
  }
  &__sortfilter {
    display: flex;
    gap: 12px;
    &--header {
      margin-top: 25px;
      @media (max-width: $w-tablet-s) {
        display: none;
      }
    }
    &--body {
      margin-bottom: 16px;
      @media (min-width: $w-tablet-b) {
        display: none;
      }
    }
  }
}
</style>
